import axios from 'axios';
import styled from 'styled-components';
import { useState, useEffect } from 'react';
import { TextField, Autocomplete } from '@mui/material';
import { IJsonStore, IOption, IStoreOption } from '../../types';
import { TFormValues } from '../../App';
import { StepContainer } from '../StepContainer';
import { Button } from '../Button';
import { formFields } from '../../constants';

interface IProps {
    className?: string;
    options: IOption[];
    selectedStoreName: string;
    getFieldError: (fieldName: keyof TFormValues) => string | undefined;
    onStepButtonClick(option: IOption): void;
    setFieldValue(field: keyof TFormValues, value: string): void;
}

const BaseStoreSelectFormOptional = ({
    className,
    options,
    selectedStoreName,
    getFieldError,
    onStepButtonClick,
    setFieldValue,
}: IProps) => {
    const [stores, setStores] = useState<IStoreOption[]>();
    const [initialSelectedStore, setInitialSelectedStore] =
        useState<IStoreOption>();

    useEffect(() => {
        if (!process.env.REACT_APP_STORES_URL_PNL) return;

        axios
            .get<IJsonStore[]>(process.env.REACT_APP_STORES_URL_PNL)
            .then(({ data }) => {
                const allStores: IStoreOption[] = data.map((store) => ({
                    name: `${store.u_smd_city} - ${store.u_smd_address} ${store.u_smd_addressnumber}`,
                    storenumber: store.u_smd_storenumber,
                    email: store.u_smd_emailstore,
                }));

                if (selectedStoreName) {
                    const store = allStores.find(
                        (store) => store.name === selectedStoreName
                    );

                    setInitialSelectedStore(store);
                }

                setStores(allStores);
            })
            .catch(() => console.error('Error fetching store list'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleStoreChange = (
        _e: React.SyntheticEvent<Element, Event>,
        value: IStoreOption | null
    ) => {
        // Reset store form value if input is cleared
        if (!value) {
            setFieldValue(formFields.store, '');

            return;
        }

        setFieldValue(formFields.store, value.name);
    };

    return (
        <StepContainer className={className}>
            <h3>
                Bij <strong>welke winkel</strong> ben je klant?
            </h3>
            {stores && (
                <Autocomplete
                    defaultValue={initialSelectedStore}
                    getOptionLabel={(option: IStoreOption) => option.name}
                    id="store-select"
                    options={stores}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            error={!!getFieldError('store')}
                            helperText={getFieldError('store')}
                            label="Kies je winkel"
                        />
                    )}
                    onChange={handleStoreChange}
                />
            )}
            {options.map((option) => (
                <Button
                    key={option.title}
                    secondary={!!option.secondaryButton}
                    onClick={() => onStepButtonClick(option)}
                >
                    {option.title}
                </Button>
            ))}
        </StepContainer>
    );
};

export const StoreSelectFormOptional = styled(BaseStoreSelectFormOptional)`
    h3 {
        margin-bottom: 24px;
    }

    ${Button} {
        margin-top: 8px;
        margin-right: 12px;
    }
`;
